/**
 * VCloud Expanded Row Data Transform
 */
import type { TFunction } from 'i18next'
import type { VCloudExpandedRow, ServiceGroup } from './types'
import {
    getServiceType,
    calculateTotals,
    calculateTypeTotals,
    isStorageType,
    isPublicIPType,
    isFloatingIPType,
    isBandwidthType,
} from './helpers'

// ============================================================================
// Data Transformation
// ============================================================================

interface TransformOptions {
    row: any
    t: TFunction
}

export const transformToVCloudRows = ({
    row,
    t,
}: TransformOptions): VCloudExpandedRow[] => {
    const serviceTemplate = row?.service_template
    const isVCloudTemplate =
        serviceTemplate?.toLowerCase().includes('vcloud') ||
        serviceTemplate?.toLowerCase().includes('v2g')

    // Only use VCloud-style for VCloud templates
    if (!isVCloudTemplate) return []

    const result: VCloudExpandedRow[] = []

    // Build service groups (main service + addon services)
    const serviceGroups: Map<string, ServiceGroup> = new Map()

    // Main service
    const mainServiceId = row?.service_id || 'main'
    const mainConfigs = (row?.configoptions || []).filter(
        (item: any) =>
            item.commitment_value !== null &&
            item.commitment_value !== undefined &&
            item.commitment_value > 0
    )

    serviceGroups.set(mainServiceId, {
        serviceId: mainServiceId,
        serviceName:
            row?.service_name ||
            t('main_service', { defaultValue: 'Dịch vụ chính' }),
        isMain: true,
        items: mainConfigs.map((item: any) => ({ ...item, isAddon: false })),
        service_pricing_model: row?.service_pricing_model,
        service_price_register: row?.service_price_register,
    })

    // Addon services
    if (row?.addons && row.addons.length > 0) {
        row.addons.forEach((addon: any) => {
            const addonConfigs = (addon.configoptions || []).filter(
                (item: any) =>
                    item.commitment_value !== null &&
                    item.commitment_value !== undefined &&
                    item.commitment_value > 0
            )
            if (addonConfigs.length > 0) {
                const addonId = addon.addon_service_id || addon.service_id
                serviceGroups.set(addonId, {
                    serviceId: addonId,
                    serviceName:
                        addon.addon_service_name ||
                        addon.service_name ||
                        t('addon_service', { defaultValue: 'Addon' }),
                    isMain: false,
                    items: addonConfigs.map((item: any) => ({
                        ...item,
                        isAddon: true,
                    })),
                    service_pricing_model:
                        addon.addon_pricing_model ||
                        addon.service_pricing_model,
                    service_price_register:
                        addon.addon_price_register ||
                        addon.service_price_register,
                })
            }
        })
    }

    // If no config options at all, return empty
    const totalItems = Array.from(serviceGroups.values()).reduce(
        (sum, g) => sum + g.items.length,
        0
    )
    if (totalItems === 0) return []

    // Build rows
    const grandTotals = {
        vCores: 0,
        ram: 0,
        storage: 0,
        publicIP: 0,
        floatingIP: 0,
        bandwidth: 0,
        price: 0,
        combinedTotal: 0,
    }

    const sortedGroups = Array.from(serviceGroups.values()).sort((a, _b) =>
        a.isMain ? -1 : 1
    )

    sortedGroups.forEach((group) => {
        if (group.items.length === 0) return

        const serviceTotals = calculateTotals(group.items)

        // Add to grand totals
        grandTotals.vCores += serviceTotals.vCores
        grandTotals.ram += serviceTotals.ram
        grandTotals.storage += serviceTotals.storage
        grandTotals.publicIP += serviceTotals.publicIP
        grandTotals.floatingIP += serviceTotals.floatingIP
        grandTotals.bandwidth += serviceTotals.bandwidth
        grandTotals.price += serviceTotals.price
        if (group.service_pricing_model === 'package') {
            grandTotals.combinedTotal += group.service_price_register || 0
        } else {
            grandTotals.combinedTotal += serviceTotals.price
        }

        // Service header row
        result.push({
            _rowId: `service-${group.serviceId}`,
            _rowType: 'service-header',
            _isMainService: group.isMain,
            service_configoption_id: `service-header-${group.serviceId}`,
            name: group.serviceName,
            totalVCores: serviceTotals.vCores,
            totalRAM: serviceTotals.ram,
            totalStorage: serviceTotals.storage,
            totalPublicIP: serviceTotals.publicIP,
            totalFloatingIP: serviceTotals.floatingIP,
            totalBandwidth: serviceTotals.bandwidth,
            commitment_price_total: serviceTotals.price,
            service_price_register: group.service_price_register,
            service_id: group.serviceId,
            service_pricing_model: group.service_pricing_model,
        })

        // Group items by type
        const typeGroups = {
            vm_instance: [] as any[],
            vm_lb: [] as any[],
            other_service: [] as any[],
        }
        group.items.forEach((item) => {
            const serviceType = getServiceType(item)
            typeGroups[serviceType].push(item)
        })

        // Add type headers and items
        const typeOrder: Array<'vm_instance' | 'vm_lb' | 'other_service'> = [
            'vm_instance',
            'vm_lb',
            'other_service',
        ]
        const typeLabels = {
            vm_instance: t('vm.vm_instance', { defaultValue: 'VM Instance' }),
            vm_lb: t('vm.vm_lb', { defaultValue: 'Load Balancer' }),
            other_service: t('vm.other_service', {
                defaultValue: 'Dịch vụ khác',
            }),
        }

        typeOrder.forEach((typeKey) => {
            const items = typeGroups[typeKey]
            if (items.length === 0) return

            const typeTotals = calculateTypeTotals(items, typeKey)

            // Type header row
            result.push({
                _rowId: `type-${group.serviceId}-${typeKey}`,
                _rowType: 'type-header',
                _serviceType: typeKey,
                _parentServiceId: group.serviceId,
                _isMainService: group.isMain,
                service_configoption_id: `type-header-${group.serviceId}-${typeKey}`,
                name: typeLabels[typeKey],
                totalVCores: typeTotals.vCores,
                totalRAM: typeTotals.ram,
                totalStorage: typeTotals.storage,
                totalPublicIP: typeTotals.publicIP,
                totalFloatingIP: typeTotals.floatingIP,
                totalBandwidth: typeTotals.bandwidth,
                service_pricing_model: group.service_pricing_model,
            })

            // Item rows
            items.forEach((item) => {
                const quantity = item.commitment_value || 0
                const calculatedTotal =
                    item.commitment_price_total ||
                    (item.commitment_price_per_unit || 0) * quantity

                const baseRow: VCloudExpandedRow = {
                    _rowId: `item-${item.service_configoption_id || item.name}`,
                    _rowType: 'item',
                    _serviceType: typeKey,
                    _parentServiceId: group.serviceId,
                    _isMainService: group.isMain,
                    service_configoption_id:
                        item.service_configoption_id || item.name,
                    name: item.name,
                    type: item.type,
                    commitment_value: item.commitment_value,
                    commitment_price_per_unit: item.commitment_price_per_unit,
                    commitment_price_total: calculatedTotal,
                    service_id: group.serviceId,
                    service_pricing_model: group.service_pricing_model,
                }

                // Parse fields for other_service
                if (typeKey === 'other_service') {
                    if (isStorageType(item.type, item.name))
                        baseRow.storage = item.commitment_value || 0
                    if (isPublicIPType(item.type, item.name))
                        baseRow.publicIP = item.commitment_value || 0
                    if (isFloatingIPType(item.type, item.name))
                        baseRow.floatingIP = item.commitment_value || 0
                    if (isBandwidthType(item.type, item.name))
                        baseRow.bandwidth = item.commitment_value || 0
                }

                result.push(baseRow)
            })
        })
    })

    // Summary row (only if multiple services)
    if (serviceGroups.size > 1) {
        result.push({
            _rowId: 'total-summary',
            _rowType: 'summary',
            service_configoption_id: 'total-summary',
            name: t('total-service-price', { defaultValue: 'Tổng cộng' }),
            commitment_price_total: grandTotals.combinedTotal,
            totalVCores: grandTotals.vCores,
            totalRAM: grandTotals.ram,
            totalStorage: grandTotals.storage,
            totalPublicIP: grandTotals.publicIP,
            totalFloatingIP: grandTotals.floatingIP,
            totalBandwidth: grandTotals.bandwidth,
        })
    }

    return result
}
